import { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SettingsInputCompositeOutlinedIcon from '@mui/icons-material/SettingsInputCompositeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import config from 'config/env';
import AppContext from 'AppContext';
import { supabase } from 'supabaseClient';
import { useChallengesState } from 'hooks/useChallengesState';
import { useBudgetState } from 'hooks/useBudgetState';

const parseMessageText = (text) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <BoldText key={index}>{part.slice(2, -2)}</BoldText>;
    }
    return part;
  });
};

export default function AIChat({ onLoadingChange }) {
  const { platformUser } = useContext(AppContext);
  const { state: challengesState } = useChallengesState();
  const { budgetState } = useBudgetState();
  const messagesEndRef = useRef(null);
  const [chatState, setChatState] = useState({
    messages: [{
      text: 'Ask me anything. Use can also include your Challenges or Budget.',
      isUser: false
    }],
    status: 'idle'
  });
  const [input, setInput] = useState('');
  const [contextVersion, setContextVersion] = useState(0);
  const [openFilter, setOpenFilter] = useState('context');
  const [selectedContext, setSelectedContext] = useState({
    challenges: false,
    budget: false
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatState.messages]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ContextContainer')) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Update context version when state changes
  useEffect(() => {
    setContextVersion(v => v + 1);
  }, [challengesState, budgetState]);

  useEffect(() => {
    if (!platformUser) {
      setChatState(prev => ({
        ...prev,
        messages: [{
          text: 'Please sign in to use the chat feature.',
          isUser: false
        }],
        status: 'error'
      }));
    }
  }, [platformUser]);

  useEffect(() => {
    onLoadingChange?.(chatState.status === 'loading');
  }, [chatState.status, onLoadingChange]);

  const handleContextSelect = (type) => {
    setSelectedContext(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSend = async () => {
    if (!input.trim() || chatState.status === 'loading') return;

    if (!platformUser) {
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, {
          text: 'Please sign in to use the chat feature.',
          isUser: false
        }],
        status: 'error'
      }));
      return;
    }

    const userMessage = input.trim();
    setInput('');

    // Add user message
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, { text: userMessage, isUser: true }],
      status: 'loading'
    }));

    try {
      const token = await supabase.auth.getSession().then(({ data }) => data.session?.access_token);

      if (!token) {
        throw new Error('Session expired');
      }

      // Get the last 10 messages for context
      const messageHistory = chatState.messages.slice(-10);

      const chatContext = {
        message: userMessage,
        messageHistory: messageHistory,
        context: {
          dataState: selectedContext.challenges ? challengesState : null,
          budgetState: selectedContext.budget ? budgetState : null
        }
      };

      console.log('Sending to chat:', {
        message: chatContext.message,
        contextVersion,
        challengesState: chatContext.context.dataState,
        budgetState: chatContext.context.budgetState
      });

      const response = await fetch(`${config.serverUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(chatContext),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get response from AI');
      }

      const data = await response.json();

      // Add AI response
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, { text: data.message, isUser: false }],
        status: 'idle'
      }));
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.message === 'Session expired' || error.message === 'Unauthorized' || error.message === 'Invalid token'
        ? 'Your session has expired. Please sign in again.'
        : 'Sorry, I encountered an error. Please try again.';

      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, {
          text: errorMessage,
          isUser: false
        }],
        status: 'error'
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatContainer>
      <MessagesContainer>
        {chatState.messages.map((message, index) => (
          <Message key={index} $isUser={message.isUser}>
            {parseMessageText(message.text)}
          </Message>
        ))}
        {chatState.status === 'loading' && (
          <LoadingDots>
            <span />
            <span />
            <span />
          </LoadingDots>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      <InputWrapper>
        <InputContainer>
          <ContextContainer onClick={(e) => e.stopPropagation()}>
            <ContextButton
              onClick={() => setOpenFilter(openFilter === 'context' ? null : 'context')}
              className={openFilter === 'context' ? 'active' : ''}
            >
              <SettingsInputCompositeOutlinedIcon />
            </ContextButton>
            <ContextDropdown $isOpen={openFilter === 'context'}>
              <PrivacyHeader>
                <PrivacyIcon />
                <PrivacyTitle>Privacy</PrivacyTitle>
              </PrivacyHeader>
              <PrivacyMessage>
                <span>You decide what to share. We run AI models on a secure private server. Chat history and data is not stored or used for training.</span>
              </PrivacyMessage>
              <ContextOption
                className={selectedContext.challenges ? 'selected' : ''}
                onClick={() => handleContextSelect('challenges')}
              >
                <span>{selectedContext.challenges ? 'Including Challenges' : 'Include Challenges?'}</span>
                {selectedContext.challenges ? <CheckIcon fontSize="14px" /> : <CheckBoxOutlineBlankIcon fontSize="16px" />}
              </ContextOption>
              <ContextOption
                className={selectedContext.budget ? 'selected' : ''}
                onClick={() => handleContextSelect('budget')}
              >
                <span>{selectedContext.budget ? 'Including Budget' : 'Include Budget?'}</span>
                {selectedContext.budget ? <CheckIcon fontSize="14px" /> : <CheckBoxOutlineBlankIcon fontSize="16px" />}
              </ContextOption>
            </ContextDropdown>
          </ContextContainer>
          <Input
            placeholder="Ask me anything..."
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyPress={handleKeyPress}
            disabled={chatState.status === 'loading'}
            rows={1}
          />
          <SendButton
            onClick={handleSend}
            $canSend={input.trim() && chatState.status !== 'loading'}
            disabled={!input.trim() || chatState.status === 'loading'}
          >
            <SendRoundedIcon />
          </SendButton>
        </InputContainer>
      </InputWrapper>
    </ChatContainer>
  );
}

const ChatContainer = styled.div.attrs({
  className: 'ChatContainer'
})`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  color: #333333;
  padding: 20px 20px 0px;
  gap: 10px;
  font-family: PT Sans;
`;

const MessagesContainer = styled.div.attrs({
  className: 'MessagesContainer'
})`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-right: 10px;
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: black;
    border-radius: 2px;
  }
`;

const Message = styled.div.attrs({
  className: props => props.$isUser ? 'UserMessage' : 'AIMessage'
})`
  padding: 12px 16px;
  border-radius: 2px;
  max-width: 80%;
  line-height: 1.5;
  font-size: 14px;
  white-space: pre-wrap;
  border-radius: 3px;
  &:last-child {
    margin-bottom: 10px;
  }
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
  
  p {
    margin: 8px 0;
  }
  
  ${props => props.$isUser ? `
    background: #f7f7f7;
    color: #333333;
    align-self: flex-end;
  ` : `
    background: rebeccapurple;
    color: white;
    text-align: left;
    align-self: flex-start;
  `}
  &:hover {
  }
`;

const InputContainer = styled.div.attrs({
  className: 'InputContainer'
})`
  display: flex;
  gap: 0px;
  align-items: center;
  background: #f5f5f5;
  border: 1px solid #004d57;
  padding: 5px 12px 5px 5px;
  border-radius: 3px;
  &:focus-within {
    border-color: #004d57;
  }
`;

const InputWrapper = styled.div.attrs({
  className: 'InputWrapper'
})`
  padding: 10px;
  background: white;
  border-top: 1px solid #f1f1f1;
  box-shadow: 0 -2px 6px rgba(60, 60, 60, 0.05);
  margin: 0px -20px;
`;

const Input = styled.textarea.attrs({
  className: 'ChatInput'
})`
  flex: 1;
  background: transparent;
  border: none;
  color: #333333;
  font-size: 14px;
  font-family: PT Sans;
  padding: 8px 0px;
  resize: none;
  min-height: 24px;
  max-height: 150px;
  overflow-y: auto;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: #9e9e9e;
  }

  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 2px;
  }
`;

const SendButton = styled.button.attrs({
  className: 'SendButton'
})`
  background: transparent;
  border: none;
  color: ${props => props.$canSend ? '#2196f3' : '#bdbdbd'};
  cursor: ${props => props.$canSend ? 'pointer' : 'default'};
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.$canSend ? '#1976d2' : '#bdbdbd'};
  }

  &:disabled {
    cursor: not-allowed;
  }

  svg {
    font-size: 20px;
  }
`;

const LoadingDots = styled.div.attrs({
  className: 'LoadingDots'
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  border-radius: 2px;
  background: #f5f5f5;
  color: #757575;
  align-self: flex-start;

  span {
    width: 4px;
    height: 4px;
    background: currentColor;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const BoldText = styled.span.attrs({
  className: 'BoldText'
})`
  font-weight: 700;
`;

const ContextContainer = styled.div.attrs({
  className: 'ContextContainer'
})`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const ContextButton = styled.button.attrs({
  className: 'ContextButton'
})`
  background: transparent;
  border: none;
  color: #8c8c8c;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: black;
  }
  &.active {
  }

  svg {
    font-size: 18px;
  }
`;

const ContextDropdown = styled.div.attrs({
  className: 'ContextDropdown'
})`
  position: absolute;
  bottom: 60px;
  left: 0px;
  background: white;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  min-width: 190px;
  z-index: 10;
  margin-bottom: 5px;
  opacity: 0;
  transform: translateY(2px);
  visibility: hidden;
  transition: all 0.2s ease-in-out;
  padding: 8px 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  ${props => props.$isOpen && `
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  `}
`;

const ContextOption = styled.div.attrs({
  className: 'ContextOption'
})`
  cursor: pointer;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  border-radius: 3px;
  padding: 5px 10px;
  font-weight: 900;
  box-sizing: border-box;
  &:hover {
    background: #f1f1f1;
  }
  &.selected {
    background: #01b088;
    color: white;
  }

`;

const PrivacyHeader = styled.div.attrs({
  className: 'PrivacyHeader'
})`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 10px 0px 7px;
  flex-direction: row;
    svg {
    font-size: 17px;
  }
`;

const PrivacyTitle = styled.span.attrs({
  className: 'PrivacyTitle'
})`
  font-size: 12px;
  font-weight: 900;
`;

const PrivacyMessage = styled.div.attrs({
  className: 'PrivacyMessage'
})`
  display: flex;
  gap: 8px;
  padding: 0px 10px 10px;
  font-size: 11px;
  color: #8c8c8c;
  border-bottom: 1px solid #f1f1f1;
  margin-bottom: 5px;
  flex-direction: column;
  
  svg {
    font-size: 14px;
  }
  &:hover {
    color: black;
    cursor: default;
  }
`;

const PrivacyIcon = styled(PrivacyTipIcon).attrs({
  className: 'PrivacyIcon'
})`
  color: black;
  transition: color 0.2s;
  

`;
